import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea } from '@mantine/core'
import { IconArrowsExchange } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { chamadosApi, type TramitarOpcoes } from '@/api/chamados'

const BLOQUEIOS: Record<string, string> = {
  chamado_encerrado: 'Chamado encerrado não muda de lugar.',
  use_a_promocao: 'Chamado de mudança anda pela promoção entre ambientes, não pela tramitação.',
  pedido_de_terraform_em_andamento: 'Há pedido de Terraform em andamento neste chamado.',
  proposta_pendente_na_conversa: 'Há um cartão de ação pendente na conversa do chamado: confirme ou recuse antes de tramitar.',
}
const MOTIVO_MINIMO = 10

function motivoDoErro(err: unknown): string {
  const d = (err as { response?: { data?: { detail?: { motivo?: string } | string } } })?.response?.data?.detail
  if (d && typeof d === 'object' && d.motivo) return d.motivo
  if (typeof d === 'string') return d
  return 'Não foi possível tramitar. Nada foi mudado.'
}

export function TramitarChamadoModal({ chamadoId, opened, onClose, onDone }: {
  chamadoId: number; opened: boolean; onClose: () => void; onDone: () => void
}) {
  const { data } = useQuery<TramitarOpcoes>({
    queryKey: ['chamado-tramitar-destinos', chamadoId],
    queryFn: async () => (await chamadosApi.getTramitarDestinos(chamadoId)).data,
    enabled: opened,
  })
  const [tenant, setTenant] = useState<string | null>(null)
  const [categoria, setCategoria] = useState<string | null>(null)
  const [motivo, setMotivo] = useState('')
  const destinos = data?.destinos ?? []
  useEffect(() => {
    if (destinos.length === 1 && tenant == null) setTenant(String(destinos[0].tenant_id))
  }, [destinos, tenant])
  const escolhido = useMemo(() => destinos.find((d) => String(d.tenant_id) === tenant), [destinos, tenant])
  const mut = useMutation({
    mutationFn: () => chamadosApi.tramitar(chamadoId, {
      tenant_destino_id: Number(tenant), motivo: motivo.trim(),
      category_id: escolhido?.precisa_categoria && categoria ? Number(categoria) : null,
    }),
    onSuccess: () => onDone(),
  })
  const bloqueio = data?.bloqueio ? BLOQUEIOS[data.bloqueio] ?? data.bloqueio : null
  const pronto = !bloqueio && !!escolhido && motivo.trim().length >= MOTIVO_MINIMO
    && (!escolhido.precisa_categoria || !!categoria)
  return (
    <Modal opened={opened} onClose={onClose} title={`Tramitar chamado #${chamadoId}`} size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          O atendimento segue como está: status, responsável e time não mudam. Mudam o tenant e a organização.
        </Text>
        {bloqueio && <Alert color="yellow">{bloqueio}</Alert>}
        <Select
          label="Para o tenant"
          placeholder="Selecione o destino"
          data={destinos.map((d) => ({
            value: String(d.tenant_id),
            label: d.org_nome ? `${d.tenant_nome} (${d.org_nome})` : d.tenant_nome,
          }))}
          value={tenant}
          onChange={(v) => { setTenant(v); setCategoria(null) }}
          searchable
          required
        />
        {escolhido?.precisa_categoria && (
          <Select
            label="Categoria no destino"
            description="A categoria atual só existe no tenant de origem."
            data={escolhido.categorias.map((c) => ({ value: String(c.id), label: c.nome }))}
            value={categoria}
            onChange={setCategoria}
            required
          />
        )}
        <Textarea
          label="Motivo"
          placeholder="Por que o chamado muda de lugar?"
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          rows={3}
          maxLength={500}
          description={`${motivo.trim().length}/500 — mínimo ${MOTIVO_MINIMO}`}
          required
        />
        {mut.isError && <Alert color="red">{motivoDoErro(mut.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancelar</Button>
          <Button disabled={!pronto} loading={mut.isPending} onClick={() => mut.mutate()}
                  leftSection={<IconArrowsExchange size={14} />}>
            Tramitar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
